import React, { useState } from 'react';
import { Edit, ExternalLink, Trash2, AlertTriangle, Calendar, DollarSign, Camera } from 'lucide-react';
import { Link } from 'react-router-dom';
import Badge from '../ui/Badge';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import { useInventory } from '../../context/InventoryContext';

interface RDWasteItem {
  id: string;
  item_id: string;
  original_project_name: string;
  description: string;
  category: 'broken' | 'discontinued' | 'abandoned' | 'obsolete';
  category_id: string;
  date_added: string;
  reason: string;
  original_cost: number;
  disposal_status: 'pending' | 'scheduled' | 'completed';
  salvage_value: number;
  researcher: string;
  project_team: string;
  storage_location: string;
  project_code: string;
  disposal_method: string;
  environmental_impact: string;
  stock_quantity: number;
  created_at: string;
  updated_at: string;
  images?: string[];
}

interface RDWasteItemCardProps {
  item: RDWasteItem;
}

const RDWasteItemCard: React.FC<RDWasteItemCardProps> = ({ item }) => {
  const { getCategoryName } = useInventory();
  const categoryName = getCategoryName(item.category_id);
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  // Debug logging to verify item data
  console.log('RDWasteItemCard rendering item:', item.id, item.item_id);
  console.log('Item images:', item.images);

  const getCategoryBadgeVariant = (category: string) => {
    switch (category) {
      case 'broken': return 'danger';
      case 'discontinued': return 'warning';
      case 'abandoned': return 'secondary';
      case 'obsolete': return 'primary';
      default: return 'secondary';
    }
  };

  const getDisposalBadgeVariant = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'scheduled': return 'primary';
      case 'completed': return 'success';
      default: return 'secondary';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'broken': return 'Broken/Damaged';
      case 'discontinued': return 'Discontinued';
      case 'abandoned': return 'Abandoned';
      case 'obsolete': return 'Obsolete';
      default: return category;
    }
  };

  const getDisposalLabel = (status: string) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'scheduled': return 'Scheduled';
      case 'completed': return 'Completed';
      default: return status;
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Card className="h-full flex flex-col transition-all duration-200 hover:shadow-md group">
      <Link to={`/rd-waste/${item.id}`} className="flex-grow" onClick={() => console.log('Navigating to:', `/rd-waste/${item.id}`)}>
        <div className="relative aspect-[4/3] overflow-hidden rounded-t-lg">
          {item.images && item.images.length > 0 && !imageError ? (
            <>
              {imageLoading && (
                <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              )}
            <img
              src={item.images[0]}
              alt={item.original_project_name}
                className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-200 ${
                  imageLoading ? 'opacity-0' : 'opacity-100'
                }`}
              onError={(e) => {
                console.error('Image failed to load:', item.images?.[0]);
                  setImageError(true);
                  setImageLoading(false);
              }}
              onLoad={() => {
                console.log('Image loaded successfully:', item.images?.[0]);
                  setImageLoading(false);
              }}
            />
            </>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center fallback-container">
              <div className="text-center">
                <Trash2 className="h-12 w-12 text-red-400 mx-auto mb-2" />
                <p className="text-xs text-red-600">No image available</p>
              </div>
            </div>
          )}
          
          {/* Fallback that shows when image fails to load */}
          {imageError && (
            <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center">
              <div className="text-center">
                <Trash2 className="h-12 w-12 text-red-400 mx-auto mb-2" />
                <p className="text-xs text-red-600">Image failed to load</p>
              </div>
            </div>
          )}
          
          <div className="absolute top-2 right-2 space-x-1">
            <Badge variant={getCategoryBadgeVariant(item.category)}>
              {getCategoryLabel(item.category)}
            </Badge>
            {item.images && item.images.length > 1 && (
              <Badge variant="secondary">
                <Camera className="h-3 w-3 mr-1" />
                {item.images.length}
              </Badge>
            )}
          </div>
          <div className="absolute bottom-2 left-2 flex items-center">
            <div className="p-2 bg-red-100 rounded-full mr-3">
              <Trash2 className="h-5 w-5 text-red-600" />
            </div>
            <div className="bg-black bg-opacity-50 rounded px-2 py-1">
              <h4 className="font-medium text-white text-shadow">{item.item_id}</h4>
              <p className="text-xs text-white text-shadow">Qty: {item.stock_quantity} • {formatDate(item.date_added)}</p>
            </div>
          </div>
        </div>
        
        <CardContent className="flex-1 flex flex-col p-4">
          <div className="mb-2">
            <Badge variant="secondary">{categoryName}</Badge>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-blue-600">
            {item.original_project_name}
          </h3>
          <p className="text-sm text-gray-500 line-clamp-2 mb-3">{item.description}</p>
          
          <div className="mt-auto space-y-3">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-gray-500">Quantity:</span>
                <span className="text-lg font-bold text-gray-900">{item.stock_quantity} units</span>
              </div>
              <div className="flex items-center text-sm text-gray-500">
                <span className="font-medium">Project:</span>
                <span className="ml-1 text-blue-600">{item.project_code}</span>
              </div>
              <div className="flex items-center text-sm text-gray-500">
                <span className="font-medium">Researcher:</span>
                <span className="ml-1 truncate">{item.researcher}</span>
              </div>
              <div className="flex items-center text-sm text-gray-500">
                <span className="font-medium">Location:</span>
                <span className="ml-1 truncate">{item.storage_location}</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div className="p-2 bg-red-50 rounded text-center">
                <div className="text-sm font-bold text-red-900">${item.original_cost.toLocaleString()}</div>
                <div className="text-xs text-red-600">Original Cost</div>
              </div>
              <div className="p-2 bg-green-50 rounded text-center">
                <div className="text-sm font-bold text-green-900">${item.salvage_value.toLocaleString()}</div>
                <div className="text-xs text-green-600">Salvage Value</div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Disposal:</span>
              <Badge variant={getDisposalBadgeVariant(item.disposal_status)}>
                {getDisposalLabel(item.disposal_status)}
              </Badge>
            </div>

            <div className="bg-gray-50 p-2 rounded text-xs text-gray-600">
              <span className="font-medium">Reason:</span> {item.reason}
            </div>
          </div>
        </CardContent>
      </Link>
      <div className="px-4 pb-4">
        <Link to={`/rd-waste/${item.id}`} className="flex-1" onClick={() => console.log('Button click navigating to:', `/rd-waste/${item.id}`)}>
          <Button variant="secondary" fullWidth>
            <ExternalLink className="h-4 w-4 mr-1" />
            View Details
          </Button>
        </Link>
      </div>
    </Card>
  );
};

export default RDWasteItemCard;